export type NamedFactory<P, T> = {
  name: string;
  factory: (props: P) => T;
};

export class Serializer {
  private factoriesByName: Record<string, NamedFactory<any, any>>;

  constructor(
    constructors: Record<string, any> = {},
    factories: NamedFactory<any, any>[] = []
  ) {
    const constructorsAsFactories = Object.entries(constructors).map(
      ([name, c]) => {
        if (!c) {
          throw new Error(`constructor ${name} is empty: value=${c}`);
        }
        return {
          name: name,
          factory: (props: any) => new c(props),
        };
      }
    );
    const allFactories = factories.concat(constructorsAsFactories);
    this.factoriesByName = Object.fromEntries(
      allFactories.map((namedFactory) => [namedFactory.name, namedFactory])
    );
  }

  serialize = (obj: any): string => {
    return JSON.stringify(this.preSerialize(obj));
  };

  private preSerialize = (obj: any, depth: string[] = []): any => {
    if (obj && Array.isArray(obj)) {
      return obj.map((item, index) =>
        this.preSerialize(item, [...depth, index.toString()])
      );
    } else if (obj && typeof obj === "object" && obj.constructor) {
      const resultingObj = Object.fromEntries(
        Object.entries(obj).map(([key, value]) => [
          key,
          this.preSerialize(value, [...depth, key]),
        ])
      );
      Object.assign(resultingObj, { __type: obj.constructor.name });
      return resultingObj;
    } else {
      return obj;
    }
  };

  deserialize = <T>(str: string): T => {
    const json = JSON.parse(str);
    return this.preDeserialize(json);
  };

  private preDeserialize = (obj: any, depth: string[] = []): any => {
    if (obj && Array.isArray(obj)) {
      return obj.map((item) => this.preDeserialize(item, depth));
    } else if (obj && typeof obj === "object") {
      const rawObject = Object.fromEntries(
        Object.entries(obj).map(([key, value]) => [
          key,
          this.preDeserialize(value, [...depth, key]),
        ])
      );
      const builderName = rawObject.__type;
      delete rawObject.__type;
      if (builderName === "Object") {
        return rawObject;
      }
      const namedFactory = this.factoriesByName[builderName];
      if (!namedFactory) {
        throw new Error(
          `${depth.join(".")} : could not find factory with name ${builderName}`
        );
      }
      // @ts-ignore
      return namedFactory.factory(rawObject);
    } else {
      return obj;
    }
  };

  copy = <T>(value: T): T => {
    return this.deserialize(this.serialize(value));
  };
}
